import {
  Children,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

type CardRailProps = {
  children: ReactNode;
  count: number;
  ink: string;
};

/** Mobile: horizontal snap carousel. Desktop: CSS grid. */
export function CardRail({ children, count, ink }: CardRailProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const alone = count <= 1;

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    const onScroll = () => {
      const cards = Array.from(el.querySelectorAll<HTMLElement>("[data-rail-card]"));
      if (!cards.length) return;
      const mid = el.scrollLeft + el.clientWidth / 2;
      let best = 0;
      let bestDist = Infinity;
      cards.forEach((card, i) => {
        const center = card.offsetLeft + card.offsetWidth / 2;
        const dist = Math.abs(center - mid);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      });
      setActive(best);
    };

    onScroll();
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [count]);

  const scrollTo = (index: number) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelectorAll<HTMLElement>("[data-rail-card]")[index];
    card?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  };

  return (
    <div className="relative">
      <div
        ref={scrollerRef}
        className={[
          "flex gap-3.5 overflow-x-auto overscroll-x-contain",
          "snap-x snap-mandatory scroll-smooth",
          "md:grid md:auto-rows-fr md:gap-5 md:overflow-visible md:snap-none",
          "md:grid-cols-2 lg:grid-cols-3",
          alone ? "px-0" : "-mx-5 pl-5 pr-8 md:mx-0 md:px-0",
          "py-1 md:py-0",
          "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        ].join(" ")}
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        {Children.map(children, (child, i) => (
          <div
            key={i}
            data-rail-card
            className={[
              "snap-center h-[340px] shrink-0 md:h-auto md:min-h-[380px] md:w-auto md:max-w-none md:min-w-0 md:shrink",
              alone ? "w-full" : "w-[min(78vw,300px)] sm:w-[min(70vw,320px)]",
            ].join(" ")}
          >
            {child}
          </div>
        ))}
      </div>

      {count > 1 && (
        <div
          className="md:hidden flex items-center justify-center gap-1.5 mt-3.5"
          role="tablist"
          aria-label="Cards"
        >
          {Array.from({ length: count }).map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Go to card ${i + 1}`}
              aria-current={active === i}
              onClick={() => scrollTo(i)}
              className="p-1.5"
            >
              <span
                className="block rounded-full transition-all duration-200"
                style={{
                  width: active === i ? 16 : 6,
                  height: 6,
                  background: active === i ? ink : "rgba(134,134,139,0.4)",
                }}
              />
            </button>
          ))}
        </div>
      )}

    </div>
  );
}
